import { useFormikContext } from "formik";
import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import DimensionsModal from "../../fields/DimensionsField";
import { formatLocationDimensions } from "../../../../utils/dimensions";

import LocationImage from "../../../../components/TablePro/components/warehouse/LocationImage";
import MobileLocationImage from "../../../../components/TablePro/components/warehouse/MobileLocationImage";
import LocationActionButtons from "./LocationActionButtons";
import ToggleActiveButton from "./ToggleActiveButton";
import AuditDetailsModal from "./AuditDetailsModal";
import PdfLinkActions from "../../../PdfLinkActions";
import Input from "src/components/form/Input";
import Select from "src/components/form/Select";

const baseLocationTypes = [
  ["floor", "Floor"],
  ["inbound", "Inbound"],
  ["offsite", "Offsite"],
  ["pallet", "Pallet"],
  ["rack", "Rack"],
  ["sofa_rack", "Sofa Rack"],
  ["trailer", "Trailer"],
  ["vault", "Vault"],
];

const mobileLocationTypes = [
  ["pallet", "Pallet"],
  ["portable_container", "Portable Container"],
  ["trailer", "Trailer"],
  ["vault", "Vault"],
];

export default function LocationInfo({
  isMobileLocation,
  item,
  isEditing,
  setIsEditing,
  onClose,
  onToggleActive,
}) {
  const disabled = !isEditing;
  const { setFieldValue, values } = useFormikContext();
  const [openDimensions, setOpenDimensions] = useState(false);
  const [openAuditDetails, setOpenAuditDetails] = useState(false);
  const currentDimensions = values?.dimensions ?? item?.dimensions;

  const handleDimensionsSave = dims => {
    setFieldValue("dimensions", dims);
  };

  const ImageComponent = isMobileLocation ? MobileLocationImage : LocationImage;
  const images = isMobileLocation ? item.mobileLocationImages : item.warehouseLocationImages;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography fontWeight={500} fontSize={20} mb={1.5}>
        Location Details
      </Typography>
      <Grid display="flex" alignItems="stretch">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ImageComponent id={item.id} images={images} />
          </Grid>
          <Grid sx={{ display: "flex", flexDirection: "column" }} size={{ xs: 12, md: 6 }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                <Input
                  disabled={disabled}
                  name="locationName"
                  label="Location Name"
                  defaultValue={item?.locationName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                <Select
                  disabled={disabled}
                  name="locationType"
                  label="Location Type"
                  options={isMobileLocation ? mobileLocationTypes : baseLocationTypes}
                  defaultValue={item?.locationType}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                <Input disabled={disabled} name="zone" label="Zone" />
              </Grid>
              {!isMobileLocation && item?.locationType === "rack" && (
                <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                  <Input
                    disabled={disabled}
                    name="slot"
                    label="Slot (Bin)"
                    defaultValue={item?.slot}
                  />
                </Grid>
              )}
              {!isMobileLocation && item?.locationType === "rack" && (
                <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                  <Input
                    disabled={disabled}
                    name="slotGroupKey"
                    label="Slot Group Key"
                    defaultValue={item?.slotGroupKey}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                <TextField
                  fullWidth
                  disabled={disabled}
                  size="small"
                  variant="outlined"
                  label='Dimensions W" x D" x H"'
                  value={formatLocationDimensions(currentDimensions)}
                  onClick={() => setOpenDimensions(true)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                <Input disabled={disabled} type="number" name="sqFtOfSlot" label="Square Footage" />
              </Grid>
              {isEditing && (
                <Grid size={{ xs: 12 }}>
                  <Input
                    disabled={disabled}
                    name="searchTerms"
                    label="Search Terms"
                    helperText="Optional nicknames or alternate terms to help users find this location."
                  />
                </Grid>
              )}
              {isMobileLocation && (
                <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                  <TextField
                    fullWidth
                    disabled
                    size="small"
                    variant="outlined"
                    label="Current Warehouse Location"
                    value={item.warehouseLocations?.map(v => v.locationName).join(", ")}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={1} pt={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <PdfLinkActions
                  resourceType={isMobileLocation ? "mobile_warehouse_location_pdfs" : "warehouse_location_pdfs"}
                  resourceId={item.id}
                  label="Generate Location PDF"
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setOpenAuditDetails(true)}
                  fullWidth
                >
                  View Audit Details
                </Button>
              </Grid>
            </Grid>
            {setIsEditing != null && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent={isEditing ? "space-between" : "flex-end"}
                gap={1}
                flexWrap="wrap"
                pt={2}
                mt="auto"
                sx={{ borderTop: 1, borderColor: "divider" }}
              >
                {isEditing && onToggleActive != null && (
                  <ToggleActiveButton active={!!item?.active} onToggle={onToggleActive} />
                )}
                <LocationActionButtons
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  onClose={onClose}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
      {openDimensions && (
        <DimensionsModal
          field={{ label: "Dimensions" }}
          value={currentDimensions}
          onSubmit={handleDimensionsSave}
          nextRow={() => {}}
          onClose={() => setOpenDimensions(false)}
        />
      )}
      {openAuditDetails && (
        <AuditDetailsModal
          onClose={() => setOpenAuditDetails(false)}
          locationId={item?.id}
          isMobileLocation={isMobileLocation}
        />
      )}
    </Paper>
  );
}
